import type { InitWhiteScreen } from '../utils/types';

/**
 * 检测页面是否白屏
 * @param {function} callback - 回到函数获取检测结果
 * @param {boolean} skeletonProject - 页面是否有骨架屏 默认值为false
 * @param {array} whiteBoxElements - 容器列表，默认值为['html', 'body', '#app', '#root']
 */
export const initWhiteScreen: InitWhiteScreen = (sentry, params) => {
    const { skeletonProject, whiteBoxElements, blankList } = params;
    let _whiteLoopNum = 0; // 白屏检测次数
    const _skeletonInitList: string[] = []; // 存储初次采样点
    let _skeletonNowList: string[] = []; // 存储当前采样点

    // 项目有骨架屏
    if (skeletonProject) {
        if (document.readyState !== 'complete') {
            sampling();
        }
    } else {
        // 页面加载完毕（文档和所有子资源已完成加载。表示 load 状态的事件即将被触发）
        // eslint-disable-next-line
        if (document.readyState === 'complete') {
            sampling();
        } else {
            window.addEventListener('load', sampling);
        }
    }

    /**
     * @description 计算元素的选择器
     * @param {Element} element 元素
     * @return {string} 选择器
     */
    function getSelector(element: Element) {
        try {
            if (element.id) {
                return '#' + element.id;
            } else if (element.className) {
                // div home => div.home
                return (
                    '.' +
                    element.className
                        .split(' ')
                        .filter((item) => !!item)
                        .join('.')
                );
            } else {
                return element.nodeName.toLowerCase();
            }
        } catch (err) {
            sentry.captureException(err, {
                extra: {
                    element: element ?? 'no element',
                    id: element?.id ?? 'no element id',
                    className: element?.className ?? 'no className',
                },
            });
        }
    }

    /**
     * @description 用来判断当前白屏页面是否在白名单内
     */
    function isValid() {
        const path = window.location.pathname;
        return !!blankList.filter((item) => path.includes(item)).length;
    }

    /**
     * @description 判断采样点是否为容器节点
     * @param {Element} element 元素
     * @return {boolean} 是否为容器节点
     */
    function isContainer(element: Element) {
        /*
          如果 element 不存在则视为是容器节点
          element 不存在可能是测试在测试的过程中打开了控制台，导致我们在采样的时候取到了控制台内的元素
         */
        // if (!element) return true;
        const selector = getSelector(element);
        // 如果没有获取到选择器，则默认不是容器节点
        if (!selector) return false;
        if (skeletonProject) {
            _whiteLoopNum ? _skeletonNowList.push(selector) : _skeletonInitList.push(selector);
        }
        return whiteBoxElements.includes(selector);
    }

    /**
     * @description 采样对比
     */
    function sampling() {
        let emptyPoints = 0;
        // 以屏幕横着中轴线为基准，每隔10%做一次采样
        for (let i = 1; i <= 9; i++) {
            const xElements = document.elementsFromPoint((window.innerWidth * i) / 10, window.innerHeight / 2);
            const yElements = document.elementsFromPoint(window.innerWidth / 2, (window.innerHeight * i) / 10);
            if (isContainer(xElements[0])) emptyPoints++;

            // 中心点只计算一次
            if (i !== 5) {
                if (isContainer(yElements[0])) emptyPoints++;
            }
        }

        const errorInfo = {
            err: new Error(`页面${window.location.href}出现白屏`),
            extra: {
                emptyPoints: '' + emptyPoints,
                screen: window.screen.width + 'x' + window.screen.height,
                viewPoint: window.innerWidth + 'x' + window.innerHeight,
                fullPath: window.location.href,
            },
        };

        // 页面正常渲染，停止轮训
        if (emptyPoints !== 17) {
            if (skeletonProject) {
                // 第一次不比较
                if (!_whiteLoopNum) {
                    openWhiteLoop();
                    return;
                }
                // 比较前后dom是否一致
                if (_skeletonNowList.join() === _skeletonInitList.join() && !isValid()) {
                    sentry.captureException(errorInfo.err, {
                        extra: {
                            ...errorInfo.extra,
                        },
                    });
                    return;
                }
            }
            if ((window as any)._loopTimer) {
                clearTimeout((window as any)._loopTimer);
                (window as any)._loopTimer = null;
            }
            // 开启轮训
        } else if (!(window as any)._loopTimer) {
            openWhiteLoop();
        }

        // 17个点都是容器节点，并且页面路径不在白名单内则视为页面白屏
        if (emptyPoints === 17 && !isValid()) {
            sentry.captureException(errorInfo.err, {
                extra: {
                    ...errorInfo.extra,
                },
            });
        }
    }

    /**
     * @description 开启白屏轮训
     */
    function openWhiteLoop() {
        if ((window as any)._loopTimer) return;
        (window as any)._loopTimer = setInterval(() => {
            if (skeletonProject) {
                _whiteLoopNum++;
                _skeletonNowList = [];
            }
            sampling();
        }, 1000);
    }
};
